import { useEffect, useState } from "react";
import { apiTech } from "@/lib/api";
import { useRealtime } from "@/lib/realtime";
import { EmptyState, StatusPill } from "@/components/admin";
import { ChevronRight, MapPin, PackageOpen, Clock } from "lucide-react";

/**
 * Tech "Parts" tab: jobs assigned to me that are currently waiting on a part.
 * Each row shows the customer, address, and the deposit-paid + balance-due
 * snapshot pulled from /api/tech/jobs/part-ordered.
 */
export default function TechParts({ onOpenJob }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  function reload() {
    return apiTech.get("/tech/jobs/part-ordered")
      .then((r) => { setJobs(r.data); setLoading(false); })
      .catch(() => { setLoading(false); });
  }

  useEffect(() => {
    reload();
    const t = setInterval(reload, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useRealtime("tech", ["job."], reload);

  return (
    <>
      <div className="flex items-end justify-between pt-1 pb-2">
        <div>
          <div className="text-base font-semibold text-[#FFFFFF] flex items-center gap-2">
            <PackageOpen className="w-4 h-4 text-[#FF6600]" /> Pending part orders
          </div>
          <div className="text-[11px] text-[#FFFFFF]/70">
            Jobs where you collected a deposit and are waiting for parts to arrive.
          </div>
        </div>
        <div className="text-[11px] text-[#FFFFFF]/45 num">{jobs.length} total</div>
      </div>

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="h-28 bg-[#002060] border border-[#FFFFFF]/15 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : jobs.length === 0 ? (
        <div className="bg-[#002060] border border-[#FFFFFF]/15 rounded-lg">
          <EmptyState message="No pending part orders. Nice work." />
        </div>
      ) : (
        <ul className="space-y-2.5" data-testid="tech-parts-list">
          {jobs.map((j) => {
            const pay = j.payment_summary;
            const paid = Number(pay?.amount_paid || 0);
            const balance = Number(pay?.balance_due ?? (pay?.total || 0) - paid);
            return (
              <li key={j.id}>
                <button
                  type="button"
                  onClick={() => onOpenJob(j.id)}
                  data-testid={`tech-part-${j.id}`}
                  className="w-full text-left bg-[#002060] border-2 border-[#FF6600]/40 rounded-lg p-3.5 hover:border-[#FF6600] active:scale-[0.99] transition-all"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold capitalize text-sm">{j.appliance}</span>
                        <StatusPill status="part-ordered" />
                      </div>
                      <div className="text-sm text-[#FFFFFF] mt-0.5 line-clamp-1">
                        {j.customer_name}
                      </div>
                      <div className="flex items-start gap-1.5 text-xs text-[#FFFFFF]/70 mt-1">
                        <MapPin className="w-3 h-3 mt-0.5 shrink-0" />
                        <span className="line-clamp-1">{j.address}</span>
                      </div>
                      {j.updated_at && (
                        <div className="flex items-center gap-1.5 text-[10px] text-[#FFFFFF]/45 mt-1 num">
                          <Clock className="w-3 h-3" /> last update {prettyDate(j.updated_at)}
                        </div>
                      )}
                    </div>
                    <ChevronRight className="w-4 h-4 text-[#FFFFFF]/45 shrink-0 mt-1" />
                  </div>

                  {pay && (
                    <div className="mt-3 pt-3 border-t border-[#FFFFFF]/15 grid grid-cols-3 gap-2 text-xs">
                      <Snippet
                        label="Total"
                        value={`$${Number(pay.total || 0).toFixed(0)}`}
                        testid={`tech-part-total-${j.id}`}
                      />
                      <Snippet
                        label="Deposit"
                        value={`$${paid.toFixed(0)}`}
                        tone="orange"
                        testid={`tech-part-deposit-${j.id}`}
                      />
                      <Snippet
                        label="Balance"
                        value={`$${Math.max(balance, 0).toFixed(0)}`}
                        tone="white-bold"
                        testid={`tech-part-balance-${j.id}`}
                      />
                    </div>
                  )}
                  {!pay && (
                    <div className="mt-3 pt-3 border-t border-[#FFFFFF]/15 text-[11px] text-[#FFFFFF]/45">
                      No invoice yet. Tap to open and add one.
                    </div>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}

function Snippet({ label, value, tone = "default", testid }) {
  const toneCls = {
    default: "text-[#FFFFFF]",
    orange: "text-[#FF6600]",
    "white-bold": "text-[#FFFFFF] font-bold",
  }[tone];
  return (
    <div className="text-center" data-testid={testid}>
      <div className="text-[9px] uppercase tracking-widest text-[#FFFFFF]/55 font-semibold">{label}</div>
      <div className={`num font-semibold text-sm mt-0.5 ${toneCls}`}>{value}</div>
    </div>
  );
}

function prettyDate(s) {
  const d = new Date(s);
  if (isNaN(d.valueOf())) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
